import { Box, Link, SxProps } from '@mui/material';
import { getGridNumericOperators, GridCellParams, GridColDef } from '@mui/x-data-grid-pro';
import { useQueryClient } from '@tanstack/react-query';
import { useIntl } from 'react-intl';
import { Link as RouterLink, useNavigate } from 'react-router-dom';

import { getCohortBuilderPath } from '@verantos-dev/core-constants';
import { CohortBuilderPageTab } from '@verantos-dev/core-enums';
import { cohortQueryKeys, fetchCohortVersion } from '@verantos-dev/data-access-cohorts';
import { type DataGridMessages, StatusDisplay } from '@verantos-dev/shared-components';
import { theme } from '@verantos-dev/shared-styles';
import { formatDate } from '@verantos-dev/shared-utils';
import { type Cohort } from '@verantos-dev/types-cohorts';

import { CohortsListActionMenu } from '../containers/CohortsListActionMenu/CohortsListActionMenu';

const stringCellStyles: SxProps = {
  display: '-webkit-box',
  WebkitLineClamp: 2,
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  lineHeight: '1.2',
  whiteSpace: 'normal',
};

const clickableCellStyle: SxProps = {
  ...stringCellStyles,
  color: theme.palette.primary.main,
  cursor: 'pointer',
  '&:hover': {
    textDecoration: 'underline',
  },
};

export const useCohortsListPageContent = () => {
  const intl = useIntl();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const messages: DataGridMessages = {
    noRows: intl.formatMessage({ id: 'projects.cohortsList.datagrid.noRows' }),
    noResults: intl.formatMessage({ id: 'projects.cohortsList.datagrid.noResults' }),
    error: intl.formatMessage({ id: 'projects.cohortsList.datagrid.errorMessage' }),
  };

  const onCohortCellClick = (params: GridCellParams) => {
    // Skip handling for cohortName field - it's handled by the Link component
    // This allows Ctrl+click and Shift+click to work properly
    if (params.field === 'cohortName') {
      return;
    }
  };

  const columnDefinition: GridColDef<Cohort>[] = [
    {
      headerName: intl.formatMessage({
        id: 'cohortsList.columnHeader.name',
        defaultMessage: 'Name',
      }),
      type: 'string',
      field: 'cohortName',
      flex: 1,
      minWidth: 330,
      renderCell: (params) => {
        const cohortName = params?.row.cohortName;
        const cohortId = params?.row.cohortProfileId;
        const projectId = params?.row.projectId;
        const cohortVersionId = params?.row.latestCohortVersionId;
        const href = `${getCohortBuilderPath(projectId, cohortId, cohortVersionId)}?pageTab=${
          CohortBuilderPageTab.definition
        }`;

        const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
          // Allow Ctrl+click (Cmd+click on Mac) or Shift+click to open in new tab/window
          if (e.ctrlKey || e.metaKey || e.shiftKey) {
            return; // Let browser handle it naturally via href
          }
          e.preventDefault();
          // Prime the TanStack cache for the destination cohort version so
          // the builder page renders warm. Fire-and-forget — mirrors the
          // old RTK `initiate(...)` behaviour.
          void queryClient.prefetchQuery({
            queryKey: cohortQueryKeys.version(cohortVersionId),
            queryFn: () => fetchCohortVersion(projectId, cohortId, cohortVersionId),
          });
          navigate(href);
        };

        return (
          <Link
            component={RouterLink}
            to={href}
            data-testid={`cohort-name-${cohortId}`}
            sx={clickableCellStyle}
            onClick={handleClick}
          >
            {cohortName}
          </Link>
        );
      },
    },
    {
      headerName: intl.formatMessage({ id: 'cohortsList.columnHeader.status' }),
      type: 'string',
      field: 'latestStatus',
      minWidth: 130,
      flex: 0,
      renderCell: (params) => {
        const status = params?.row.latestStatus;
        const cohortId = params?.row.cohortProfileId;
        return (
          <StatusDisplay
            id={`cohort-status-${cohortId}`}
            status={`cohortstatus${status.toLowerCase()}`}
          />
        );
      },
    },
    {
      headerName: intl.formatMessage({ id: 'cohortsList.columnHeader.subjects' }),
      type: 'number',
      field: 'latestSubjectCount',
      minWidth: 150,
      flex: 0.5,
      align: 'right',
      headerAlign: 'left',
      filterOperators: getGridNumericOperators().map((operator) => ({
        ...operator,
        getApplyFilterFn: (filterItem, column) => {
          if (
            operator.requiresFilterValue !== false &&
            (filterItem.value === '' || filterItem.value == null)
          ) {
            return null;
          }
          return operator.getApplyFilterFn(filterItem, column);
        },
      })),
      valueGetter: (value: number) => (value >= 0 ? value : null),
      renderCell: (params) => {
        const latestSubjectCount =
          params?.row.latestSubjectCount >= 0
            ? intl.formatNumber(params.row.latestSubjectCount)
            : '--';
        const cohortId = params?.row.cohortProfileId;
        return (
          <Box data-testid={`cohort-latestSubjectCount-${cohortId}`} sx={stringCellStyles}>
            {latestSubjectCount}
          </Box>
        );
      },
    },
    {
      headerName: intl.formatMessage({ id: 'cohortsList.columnHeader.owner' }),
      type: 'string',
      field: 'createdBy.firstName',
      minWidth: 330,
      flex: 0.5,
      valueGetter: (_, row) => {
        const owner = `${row.createdBy.firstName} ${row.createdBy.lastName}`;
        return owner;
      },
      renderCell: (params) => {
        const owner = `${params?.row.createdBy.firstName} ${params?.row.createdBy.lastName}`;
        const cohortId = params?.row.cohortProfileId;
        return (
          <Box data-testid={`cohort-owner-${cohortId}`} sx={stringCellStyles}>
            {owner}
          </Box>
        );
      },
    },
    {
      headerName: intl.formatMessage({ id: 'cohortsList.columnHeader.modifiedOn' }),
      type: 'dateTime',
      field: 'modifiedBy.transactionDate',
      minWidth: 240,
      flex: 0.5,
      valueGetter: (_, row) => {
        return new Date(row.modifiedBy.transactionDate);
      },
      renderCell: (params) => {
        const modifiedOn = params.row.modifiedBy.transactionDate
          ? formatDate(params.row.modifiedBy.transactionDate, 'dateTimeFull')
          : '--';
        const cohortId = params?.row.cohortProfileId;
        return (
          <Box data-testid={`cohort-owner-${cohortId}`} sx={stringCellStyles}>
            {modifiedOn}
          </Box>
        );
      },
    },
    {
      field: '',
      headerName: intl.formatMessage({ id: 'common.grid.columnName.actions' }),
      headerAlign: 'right',
      align: 'right',
      flex: 0,
      width: 80,
      type: 'actions',
      renderCell: (param) => {
        return <CohortsListActionMenu cohort={param.row} />;
      },
      renderHeader: () => null,
    },
  ];

  return {
    messages,
    onCohortCellClick,
    columnDefinition,
  };
};
